import { PayloadGetClubs } from '@/types'

/**
 * A facet is a filter that composes with a club search, independently of which search it is.
 * That is a different axis from *what* is being searched: a facet narrows whatever the search
 * returned, so the two must stay separable rather than one being modelled as a variant of the
 * other.
 *
 * Data-driven so adding one is adding an entry plus the field it sets — no call site learns which
 * facets exist. Mirrors `clubSearchFacets.ts` in the mobile app; the two should stay in step.
 */
export enum ClubSearchFacet {
  /** Clubs taking part in a benefit program, whatever the caller holds. */
  participating = 'participating',
}

export type FacetContext = {
  /** Whether the member holds anything still claimable. */
  hasClaimableBenefit: boolean
  /**
   * Whether the benefits feature is on for this user at all — the `enable-benefits-feature` server
   * flag. Part of the context rather than a check at the call site, because availability is what
   * this registry already models: a facet decides for itself whether it is worth offering, and a
   * page that has to remember to filter the list is a page that can forget.
   */
  benefitsEnabled: boolean
}

type FacetDefinition = {
  value: ClubSearchFacet
  /** i18n key, resolved by the caller so this module stays render-agnostic. */
  labelKey: string
  apply: (payload: PayloadGetClubs) => PayloadGetClubs
  /**
   * Whether the facet is worth offering at all. A filter that can only ever empty the list is
   * worse than no filter — which is why the caller-relative one used to be hidden from members
   * holding nothing, and why the club-fact one does not need to be.
   */
  isAvailable: (context: FacetContext) => boolean
}

const DEFINITIONS: Record<ClubSearchFacet, FacetDefinition> = {
  [ClubSearchFacet.participating]: {
    value: ClubSearchFacet.participating,
    labelKey: 'benefits.filterParticipating',
    apply: (payload) => ({ ...payload, requiresAnyBenefit: true }),
    // Offered to everyone the feature is on for, including members holding nothing. It describes
    // the CLUB, so it never empties the list for want of a grant — which is what made the old
    // caller-relative filter worth hiding — but it is a meaningless question for someone who
    // cannot hold a benefit yet.
    isAvailable: (context) => context.benefitsEnabled,
  },
}

/** The facets worth showing, given what the member actually holds. */
export const availableFacets = (context: FacetContext) =>
  Object.values(DEFINITIONS).filter((definition) => definition.isAvailable(context))

/** Fold every active facet into one search payload. */
export const applyFacets = (payload: PayloadGetClubs, active: ClubSearchFacet[]): PayloadGetClubs =>
  active.reduce((accumulated, facet) => DEFINITIONS[facet].apply(accumulated), payload)
